import { getTokenFor, signInSilent, getPrimaryAccount, setGapiToken } from './auth';

const COLORS = ['#C9A84C', '#4a9eff', '#4CAF7C', '#8b7cf6', '#e05252'];

// Short label for an account, used in provenance tags: "marcio@vergemktg.com" -> "vergemktg"
export function accountShortName(email) {
  if (!email) return '';
  const domain = email.split('@')[1] || '';
  return domain.split('.')[0] || email.split('@')[0];
}

// Run fn with a specific account's token active on the shared gapi client,
// then restore the primary account's token. gapi holds ONE global token, so
// callers must not interleave accounts — keep usage serial.
export async function withAccountToken(email, fn) {
  let token = email ? getTokenFor(email) : null;
  if (email && !token) {
    try { token = await signInSilent(email); } catch { token = null; }
  }
  if (token) setGapiToken(token);
  try {
    return await fn();
  } finally {
    const primary = getPrimaryAccount();
    const primaryToken = primary ? getTokenFor(primary.email) : null;
    if (primaryToken) setGapiToken(primaryToken);
  }
}

// Decode Gmail's base64url bodies as UTF-8 (bare atob() mangles accented names + smart quotes)
function decodeBase64Url(data) {
  try {
    const b64 = data.replace(/-/g, '+').replace(/_/g, '/');
    const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
    return new TextDecoder('utf-8').decode(bytes);
  } catch {
    return '';
  }
}

function getInitials(name) {
  if (!name) return '??';
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

function timeAgo(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffHrs = diffMs / (1000 * 60 * 60);
  if (diffHrs < 1) return `${Math.round(diffMs / 60000)}m ago`;
  if (diffHrs < 24) {
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return date.toLocaleDateString('en-US', { weekday: 'short' });
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export async function fetchCalendarEvents() {
  const now = new Date();
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
  const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59).toISOString();

  const response = await window.gapi.client.calendar.events.list({
    calendarId: 'primary',
    timeMin: startOfDay,
    timeMax: endOfDay,
    singleEvents: true,
    orderBy: 'startTime',
    maxResults: 10,
  });

  return (response.result.items || []).map((event, i) => {
    const isAllDay = !event.start.dateTime; // all-day events only have .date (YYYY-MM-DD)
    const start = event.start.dateTime || event.start.date;
    const end = event.end.dateTime || event.end.date;
    // All-day .date strings parse as UTC midnight → wrong day in US timezones. Parse as local.
    const startISO = isAllDay ? `${start}T00:00:00` : start;
    const startTime = isAllDay ? 'All day' : new Date(start).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    const endTime = isAllDay ? 'All day' : new Date(end).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    const organizer = event.organizer?.displayName || event.organizer?.email?.split('@')[0] || '';
    const attendeeCount = event.attendees?.length || 0;

    return {
      id: event.id,
      summary: event.summary || '(No title)',
      start: startTime,
      startISO,           // raw timestamp for date math (display uses `start`)
      isAllDay,
      end: endTime,
      location: event.location || '',
      organizer,
      color: COLORS[i % COLORS.length],
      sub: [
        organizer && `Organized by ${organizer}`,
        attendeeCount > 0 && `${attendeeCount} attendees`,
        event.location && event.location.split(',')[0],
      ].filter(Boolean).join(' · '),
    };
  });
}

export async function fetchGmailThreads() {
  // Get important messages from last 2 days
  const response = await window.gapi.client.gmail.users.messages.list({
    userId: 'me',
    q: 'is:important newer_than:2d',
    maxResults: 8,
  });

  const messages = response.result.messages || [];
  const threads = [];

  for (const msg of messages.slice(0, 6)) {
    try {
      const detail = await window.gapi.client.gmail.users.messages.get({
        userId: 'me',
        id: msg.id,
        format: 'metadata',
        metadataHeaders: ['From', 'Subject', 'Date'],
      });

      const headers = detail.result.payload.headers;
      const fromHeader = headers.find(h => h.name === 'From')?.value || '';
      const subject = headers.find(h => h.name === 'Subject')?.value || '';
      const date = headers.find(h => h.name === 'Date')?.value || '';

      // Parse from: "Name <email>" or just "email"
      const nameMatch = fromHeader.match(/^"?([^"<]+)"?\s*<?/);
      const from = nameMatch ? nameMatch[1].trim() : fromHeader.split('@')[0];
      const emailMatch = fromHeader.match(/<([^>]+)>/);
      const email = emailMatch ? emailMatch[1] : (fromHeader.includes('@') ? fromHeader.trim() : '');

      const labels = detail.result.labelIds || [];
      let priority = 'dim';
      if (labels.includes('IMPORTANT') && labels.includes('CATEGORY_PERSONAL')) priority = 'gold';
      else if (labels.includes('IMPORTANT')) priority = 'blue';
      if (subject.toLowerCase().includes('urgent') || subject.toLowerCase().includes('asap')) priority = 'red';

      // Parse date string to ISO (header dates are strings like "Mon, 1 Jun 2026 14:23:00 -0700")
      let isoDate = null;
      if (date) {
        const parsed = new Date(date);
        if (!isNaN(parsed.getTime())) isoDate = parsed.toISOString();
      }

      threads.push({
        from,
        email,
        subject,
        time: timeAgo(date),
        date: isoDate,
        priority,
        initials: getInitials(from),
        color: COLORS[threads.length % COLORS.length],
      });
    } catch (e) {
      console.warn('Failed to fetch message:', msg.id, e);
    }
  }

  return threads;
}

// ──── Unanswered Threads — emails sent TO you that you haven't replied to ────
// This is what "needs response" actually means. Pulls threads from the last 14 days
// where the LAST message in the thread is from someone other than you.
export async function fetchUnansweredThreads(myEmail) {
  try {
    // Get threads from the last 14 days in primary inbox
    const response = await window.gapi.client.gmail.users.threads.list({
      userId: 'me',
      q: 'category:primary newer_than:14d -in:chats -in:spam -in:trash',
      maxResults: 30,
    });

    const threadIds = (response.result.threads || []).map(t => t.id);
    const results = [];

    for (const threadId of threadIds) {
      try {
        const detail = await window.gapi.client.gmail.users.threads.get({
          userId: 'me',
          id: threadId,
          format: 'metadata',
          metadataHeaders: ['From', 'To', 'Subject', 'Date', 'Message-ID'],
        });

        const messages = detail.result.messages || [];
        if (messages.length === 0) continue;

        // Look at the LAST message in the thread
        const lastMsg = messages[messages.length - 1];
        const lastHeaders = lastMsg.payload?.headers || [];
        const lastFrom = lastHeaders.find(h => h.name === 'From')?.value || '';
        const lastDate = lastHeaders.find(h => h.name === 'Date')?.value || '';

        // Skip if the last message was from me — I already replied
        if (myEmail && lastFrom.toLowerCase().includes(myEmail.toLowerCase())) continue;

        // Get subject from first message (cleanest)
        const firstHeaders = messages[0].payload?.headers || [];
        const subject = firstHeaders.find(h => h.name === 'Subject')?.value || '(no subject)';
        const cleanSubject = subject.replace(/^(Re:|Fwd?:)\s*/i, '').trim();

        // Parse sender from last message
        const nameMatch = lastFrom.match(/^"?([^"<]+?)"?\s*<?/);
        const name = nameMatch ? nameMatch[1].trim() : lastFrom.split('@')[0];
        const emailMatch = lastFrom.match(/<([^>]+)>/);
        const senderEmail = emailMatch ? emailMatch[1] : (lastFrom.includes('@') ? lastFrom.trim() : '');

        // Skip noreply/automated senders
        if (/noreply|no-reply|notifications?@|mailer-daemon|donotreply/i.test(senderEmail)) continue;
        if (/automated|newsletter|digest|updates?@/i.test(senderEmail)) continue;

        // Parse date to ISO
        let isoDate = null;
        if (lastDate) {
          const parsed = new Date(lastDate);
          if (!isNaN(parsed.getTime())) isoDate = parsed.toISOString();
        }

        // Check labels for priority signal
        const labels = lastMsg.labelIds || [];
        const isImportant = labels.includes('IMPORTANT');
        const isUnread = labels.includes('UNREAD');
        const isStarred = labels.includes('STARRED');

        results.push({
          threadId,
          from: name,
          email: senderEmail,
          subject: cleanSubject,
          time: timeAgo(lastDate),
          date: isoDate,
          isImportant,
          isUnread,
          isStarred,
          messageCount: messages.length,
          initials: getInitials(name),
          // For drafting from the REAL message and threading the reply:
          lastMessageId: lastMsg.id,
          rfcMessageId: lastHeaders.find(h => h.name === 'Message-ID' || h.name === 'Message-Id')?.value || null,
        });
      } catch (e) {
        console.warn('Failed to fetch thread:', threadId, e);
      }
    }

    // Sort by date — oldest unanswered first (those are the urgent ones)
    return results.sort((a, b) => new Date(a.date || 0).getTime() - new Date(b.date || 0).getTime());
  } catch (e) {
    console.warn('fetchUnansweredThreads failed:', e);
    return [];
  }
}

export async function fetchGmailProfile() {
  const response = await window.gapi.client.gmail.users.getProfile({
    userId: 'me',
  });
  return {
    email: response.result.emailAddress,
    totalMessages: response.result.messagesTotal,
    totalThreads: response.result.threadsTotal,
  };
}

export async function fetchDriveDocs() {
  const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

  const response = await window.gapi.client.drive.files.list({
    q: `modifiedTime > '${sevenDaysAgo}' and mimeType = 'application/vnd.google-apps.document' and trashed = false`,
    orderBy: 'modifiedTime desc',
    pageSize: 8,
    fields: 'files(id,name,modifiedTime,lastModifyingUser,webViewLink,parents)',
  });

  return (response.result.files || []).map((file, i) => ({
    title: file.name,
    modifiedBy: file.lastModifyingUser?.displayName || 'Unknown',
    modified: timeAgo(file.modifiedTime),
    icon: '📄',
    color: COLORS[i % COLORS.length],
    url: file.webViewLink,
    project: 'Google Drive',
  }));
}

export async function fetchTaskLists() {
  const response = await window.gapi.client.tasks.tasklists.list({
    maxResults: 10,
  });

  const lists = response.result.items || [];
  const allTasks = [];

  for (const list of lists) {
    try {
      const tasksResponse = await window.gapi.client.tasks.tasks.list({
        tasklist: list.id,
        maxResults: 20,
        showCompleted: true,
      });

      const tasks = tasksResponse.result.items || [];
      for (const task of tasks) {
        if (!task.title) continue;
        const due = task.due ? new Date(task.due) : null;
        let tag = 'Someday';
        let tagColor = 'dim';

        if (due) {
          const now = new Date();
          const diffDays = Math.floor((due - now) / (1000 * 60 * 60 * 24));
          if (diffDays < 0) { tag = 'Overdue'; tagColor = 'red'; }
          else if (diffDays === 0) { tag = 'Today'; tagColor = 'gold'; }
          else if (diffDays === 1) { tag = 'Tomorrow'; tagColor = 'gold'; }
          else if (diffDays <= 7) { tag = due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); tagColor = 'blue'; }
          else { tag = due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); tagColor = 'purple'; }
        }

        allTasks.push({
          id: task.id,
          listId: list.id,
          text: task.title,
          tag,
          tagColor,
          done: task.status === 'completed',
          notes: task.notes || '',
        });
      }
    } catch (e) {
      console.warn('Failed to fetch tasks for list:', list.title, e);
    }
  }

  return allTasks;
}

export async function createGoogleTask(title, notes, dueDate) {
  // Get the first (default) task list
  const listsResponse = await window.gapi.client.tasks.tasklists.list({ maxResults: 1 });
  const lists = listsResponse.result.items || [];
  if (lists.length === 0) throw new Error('No task lists found');

  const listId = lists[0].id;
  const resource = {
    title,
    status: 'needsAction',
  };
  if (notes) resource.notes = notes;
  if (dueDate) resource.due = new Date(dueDate).toISOString();

  const response = await window.gapi.client.tasks.tasks.insert({
    tasklist: listId,
    resource,
  });

  return {
    id: response.result.id,
    listId,
    text: response.result.title,
    notes: response.result.notes || '',
    done: false,
  };
}

export async function toggleTaskComplete(listId, taskId, completed) {
  await window.gapi.client.tasks.tasks.patch({
    tasklist: listId,
    task: taskId,
    resource: {
      status: completed ? 'completed' : 'needsAction',
      completed: completed ? new Date().toISOString() : null,
    },
  });
}

export async function createGmailDraft(to, subject, body, opts = {}) {
  const headers = [`To: ${to}`, `Subject: ${subject}`];
  if (opts.inReplyTo) {
    headers.push(`In-Reply-To: ${opts.inReplyTo}`);
    headers.push(`References: ${opts.inReplyTo}`);
  }
  const email = [
    ...headers,
    'Content-Type: text/plain; charset=utf-8',
    '',
    body,
  ].join('\r\n');

  const encoded = btoa(unescape(encodeURIComponent(email)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

  const message = { raw: encoded };
  if (opts.threadId) message.threadId = opts.threadId; // keep the reply in its thread

  const response = await window.gapi.client.gmail.users.drafts.create({
    userId: 'me',
    resource: { message },
  });

  return {
    id: response.result.id,
    messageId: response.result.message?.id,
  };
}

export async function sendGmailEmail(to, subject, body, opts = {}) {
  const headers = [`To: ${to}`, `Subject: ${subject}`];
  if (opts.inReplyTo) {
    headers.push(`In-Reply-To: ${opts.inReplyTo}`);
    headers.push(`References: ${opts.inReplyTo}`);
  }
  const email = [
    ...headers,
    'Content-Type: text/plain; charset=utf-8',
    '',
    body,
  ].join('\r\n');

  const encoded = btoa(unescape(encodeURIComponent(email)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

  const resource = { raw: encoded };
  if (opts.threadId) resource.threadId = opts.threadId; // reply lands in the original thread

  const response = await window.gapi.client.gmail.users.messages.send({
    userId: 'me',
    resource,
  });

  return {
    id: response.result.id,
    threadId: response.result.threadId,
  };
}

// ──── Create a Google Doc with the given title + content body ────
// Uses the Drive API to create the doc (Drive scope is already granted),
// then opens it in a new tab so Marcio can review/edit. Returns the URL.
export async function createGoogleDoc(title, content) {
  // Step 1: Create the doc as a Google Docs file in My Drive
  const createResp = await window.gapi.client.drive.files.create({
    resource: {
      name: title.endsWith('.docx') ? title.replace(/\.docx$/, '') : title,
      mimeType: 'application/vnd.google-apps.document',
    },
    fields: 'id,webViewLink,name',
  });

  const fileId = createResp.result.id;
  const url = createResp.result.webViewLink || `https://docs.google.com/document/d/${fileId}/edit`;

  // Step 2: Try to load Docs API and write the body content
  // (graceful: if Docs API isn't loaded, we still return the empty doc URL)
  try {
    if (!window.gapi.client.docs) {
      await window.gapi.client.load('https://docs.googleapis.com/$discovery/rest?version=v1');
    }
    await window.gapi.client.docs.documents.batchUpdate({
      documentId: fileId,
      resource: {
        requests: [
          {
            insertText: {
              location: { index: 1 },
              text: content + '\n',
            },
          },
        ],
      },
    });
  } catch (e) {
    console.warn('Docs API write failed (doc still created, but empty):', e);
  }

  return url;
}

export async function createGoogleCalendarEvent(summary, startTime, endTime, description = '') {
  const response = await window.gapi.client.calendar.events.insert({
    calendarId: 'primary',
    resource: {
      summary,
      description,
      start: { dateTime: startTime, timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone },
      end: { dateTime: endTime, timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone },
    },
  });
  return response.result;
}

export async function searchGmailMessages(query, maxResults = 5) {
  const response = await window.gapi.client.gmail.users.messages.list({
    userId: 'me',
    q: query,
    maxResults,
  });
  const messageIds = response.result.messages || [];
  const details = await Promise.all(
    messageIds.map(async (m) => {
      const msg = await window.gapi.client.gmail.users.messages.get({
        userId: 'me',
        id: m.id,
        format: 'metadata',
        metadataHeaders: ['From', 'Subject', 'Date'],
      });
      const headers = msg.result.payload?.headers || [];
      const get = (name) => headers.find(h => h.name === name)?.value || '';
      return {
        id: m.id,
        from: get('From'),
        subject: get('Subject'),
        date: get('Date'),
        snippet: msg.result.snippet,
      };
    })
  );
  return details;
}

export async function searchDriveFiles(query, maxResults = 5) {
  const response = await window.gapi.client.drive.files.list({
    q: `fullText contains '${query.replace(/'/g, "\\'")}' and trashed = false`,
    fields: 'files(id,name,modifiedTime,webViewLink,mimeType,lastModifyingUser)',
    pageSize: maxResults,
    orderBy: 'modifiedTime desc',
  });
  return (response.result.files || []).map(f => ({
    id: f.id,
    title: f.name,
    modified: f.modifiedTime,
    url: f.webViewLink,
    type: f.mimeType,
    modifiedBy: f.lastModifyingUser?.displayName || 'Unknown',
  }));
}

export async function fetchSheets() {
  const response = await window.gapi.client.drive.files.list({
    q: "mimeType = 'application/vnd.google-apps.spreadsheet' and trashed = false",
    orderBy: 'modifiedTime desc',
    pageSize: 5,
    fields: 'files(id,name,modifiedTime,lastModifyingUser,webViewLink)',
  });

  return (response.result.files || []).map((file, i) => ({
    id: file.id,
    title: file.name,
    modifiedBy: file.lastModifyingUser?.displayName || 'Unknown',
    modified: timeAgo(file.modifiedTime),
    url: file.webViewLink,
    color: COLORS[i % COLORS.length],
  }));
}

// ──── Deep Content Fetching (for ARIA agentic research) ────

export async function fetchDriveFileContent(fileId, mimeType) {
  try {
    // Google Docs → export as plain text
    if (mimeType === 'application/vnd.google-apps.document') {
      const resp = await window.gapi.client.drive.files.export({
        fileId,
        mimeType: 'text/plain',
      });
      return resp.body || resp.result || '';
    }
    // Google Sheets → export as CSV
    if (mimeType === 'application/vnd.google-apps.spreadsheet') {
      const resp = await window.gapi.client.drive.files.export({
        fileId,
        mimeType: 'text/csv',
      });
      return resp.body || resp.result || '';
    }
    // Google Slides → export as plain text
    if (mimeType === 'application/vnd.google-apps.presentation') {
      const resp = await window.gapi.client.drive.files.export({
        fileId,
        mimeType: 'text/plain',
      });
      return resp.body || resp.result || '';
    }
    // Regular files (PDF, txt, etc.) — download content
    const resp = await window.gapi.client.drive.files.get({
      fileId,
      alt: 'media',
    });
    return resp.body || resp.result || '';
  } catch (e) {
    console.warn('Failed to fetch file content:', fileId, e);
    return null;
  }
}

export async function fetchGmailMessageBody(messageId) {
  try {
    const msg = await window.gapi.client.gmail.users.messages.get({
      userId: 'me',
      id: messageId,
      format: 'full',
    });

    const payload = msg.result.payload;
    let body = '';

    // Try to get plain text body
    const extractBody = (part) => {
      if (part.mimeType === 'text/plain' && part.body?.data) {
        return decodeBase64Url(part.body.data);
      }
      if (part.parts) {
        for (const sub of part.parts) {
          const text = extractBody(sub);
          if (text) return text;
        }
      }
      return '';
    };

    body = extractBody(payload);

    // Fallback to snippet if body extraction fails
    if (!body && msg.result.snippet) {
      body = msg.result.snippet;
    }

    // Get headers
    const headers = payload.headers || [];
    const get = (name) => headers.find(h => h.name === name)?.value || '';

    return {
      id: messageId,
      from: get('From'),
      to: get('To'),
      subject: get('Subject'),
      date: get('Date'),
      body: body.slice(0, 8000), // Cap at 8k chars to avoid blowing up context
    };
  } catch (e) {
    console.warn('Failed to fetch message body:', messageId, e);
    return null;
  }
}

// Deep search — searches Gmail + Drive with live progress callback
export async function deepResearch(query, onStep) {
  const step = (type, text) => { if (onStep) onStep({ type, text }); };
  const allSteps = [];
  const track = (type, text) => { allSteps.push({ type, text }); step(type, text); };

  // Step 1: Search Gmail
  track('search', `Searching Gmail for "${query}"…`);
  let emails = [];
  try {
    const gmailResults = await searchGmailMessages(query, 5);
    emails = gmailResults;
    if (emails.length > 0) {
      track('found', `Found ${emails.length} email${emails.length !== 1 ? 's' : ''} in Gmail`);
      emails.slice(0, 3).forEach(e => {
        track('detail', `📧 ${e.subject} — ${e.from?.split('<')[0]?.trim()}`);
      });
    } else {
      track('empty', `No emails matched "${query}"`);
    }
  } catch (e) {
    track('warn', `Gmail search failed — ${e.message}`);
  }

  // Step 2: Search Drive
  track('search', `Searching Google Drive…`);
  let docs = [];
  try {
    const driveResults = await searchDriveFiles(query, 5);
    docs = driveResults;
    if (docs.length > 0) {
      track('found', `Found ${docs.length} document${docs.length !== 1 ? 's' : ''} in Drive`);
      docs.slice(0, 3).forEach(d => {
        track('detail', `📄 ${d.title} — ${d.modifiedBy}`);
      });
    } else {
      track('empty', `No documents matched "${query}"`);
    }
  } catch (e) {
    track('warn', `Drive search failed — ${e.message}`);
  }

  // Step 3: If nothing found, try broader search terms
  if (emails.length === 0 && docs.length === 0) {
    const words = query.split(/\s+/).filter(w => w.length > 3);
    for (const word of words.slice(0, 2)) {
      track('retry', `Trying broader search: "${word}"…`);
      try {
        const broader = await searchDriveFiles(word, 3);
        if (broader.length > 0) {
          docs.push(...broader);
          track('found', `Found ${broader.length} doc${broader.length !== 1 ? 's' : ''} for "${word}"`);
          broader.forEach(d => track('detail', `📄 ${d.title}`));
          break;
        }
      } catch (e) { /* continue */ }
      try {
        const broaderMail = await searchGmailMessages(word, 3);
        if (broaderMail.length > 0) {
          emails.push(...broaderMail);
          track('found', `Found ${broaderMail.length} email${broaderMail.length !== 1 ? 's' : ''} for "${word}"`);
          break;
        }
      } catch (e) { /* continue */ }
    }
  }

  // Step 4: Fetch content from top results
  const contents = [];

  if (emails.length > 0) {
    track('read', `Reading ${Math.min(emails.length, 2)} email${Math.min(emails.length, 2) !== 1 ? 's' : ''} in full…`);
    for (const email of emails.slice(0, 2)) {
      const fullEmail = await fetchGmailMessageBody(email.id);
      if (fullEmail) {
        contents.push({
          type: 'email',
          from: fullEmail.from,
          to: fullEmail.to,
          subject: fullEmail.subject,
          date: fullEmail.date,
          body: fullEmail.body,
        });
        track('done', `Read: ${fullEmail.subject}`);
      }
    }
  }

  if (docs.length > 0) {
    track('read', `Opening ${Math.min(docs.length, 2)} document${Math.min(docs.length, 2) !== 1 ? 's' : ''}…`);
    for (const doc of docs.slice(0, 2)) {
      const content = await fetchDriveFileContent(doc.id, doc.type);
      if (content) {
        contents.push({
          type: 'document',
          title: doc.title,
          url: doc.url,
          modified: doc.modified,
          modifiedBy: doc.modifiedBy,
          body: typeof content === 'string' ? content.slice(0, 10000) : JSON.stringify(content).slice(0, 10000),
        });
        track('done', `Read: ${doc.title} (${(typeof content === 'string' ? content.length : 0).toLocaleString()} chars)`);
      }
    }
  }

  if (contents.length > 0) {
    track('analyze', `Sending ${contents.length} source${contents.length !== 1 ? 's' : ''} to ARIA for analysis…`);
  }

  return { steps: allSteps, emails, docs, contents };
}

export async function fetchGoogleUserInfo() {
  try {
    const token = window.gapi.client.getToken();
    if (!token) return null;
    const resp = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
      headers: { Authorization: `Bearer ${token.access_token}` },
    });
    const info = await resp.json();
    // A 401/expired token yields an error object, not a profile. Returning a
    // fabricated "there" profile here poisons the account store — refuse it.
    if (!info?.email) return null;
    return {
      name: info.name || info.email?.split('@')[0] || 'User',
      email: info.email || '',
      picture: info.picture || '',
      firstName: (info.given_name || info.name?.split(' ')[0] || 'there'),
      initials: info.name
        ? info.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
        : (info.email?.[0] || 'U').toUpperCase(),
    };
  } catch (e) {
    console.warn('Failed to fetch user info:', e);
    return null;
  }
}

export async function fetchAllData() {
  // First grab the profile so we know "my" email — needed for unanswered detection
  const gmailProfile = await fetchGmailProfile().catch(() => null);
  const myEmail = gmailProfile?.email;

  const [calendar, gmailThreads, unanswered, drive, tasks, sheets] = await Promise.allSettled([
    fetchCalendarEvents(),
    fetchGmailThreads(),
    fetchUnansweredThreads(myEmail),
    fetchDriveDocs(),
    fetchTaskLists(),
    fetchSheets(),
  ]);

  return {
    calendar: calendar.status === 'fulfilled' ? calendar.value : [],
    gmail: {
      unreadCount: gmailProfile?.totalMessages || 0,
      priorityThreads: gmailThreads.status === 'fulfilled' ? gmailThreads.value : [],
      unansweredThreads: unanswered.status === 'fulfilled' ? unanswered.value : [],
      myEmail,
    },
    drive: drive.status === 'fulfilled' ? drive.value : [],
    tasks: tasks.status === 'fulfilled' ? tasks.value : [],
    sheets: sheets.status === 'fulfilled' ? sheets.value : [],
    stats: {
      emailsToday: gmailProfile?.totalMessages || 0,
      emailsFlagged: gmailThreads.status === 'fulfilled' ? gmailThreads.value.length : 0,
      meetings: calendar.status === 'fulfilled' ? calendar.value.length : 0,
      meetingsTotal: calendar.status === 'fulfilled' ? calendar.value.length : 0,
    },
  };
}

// Tag every item in one account's result with its source account, so merged
// views can show provenance and write actions can route to the right inbox.
function tagResult(result, email) {
  const short = accountShortName(email);
  const tag = (item) => ({ ...item, account: email, accountShort: short });
  return {
    ...result,
    calendar: (result.calendar || []).map(tag),
    gmail: {
      ...result.gmail,
      priorityThreads: (result.gmail?.priorityThreads || []).map(tag),
      unansweredThreads: (result.gmail?.unansweredThreads || []).map(tag),
    },
    drive: (result.drive || []).map(tag),
    tasks: (result.tasks || []).map(tag),
    sheets: (result.sheets || []).map(tag),
  };
}

// ──── Multi-account aggregate ────
// Fetches every connected Google account SERIALLY (gapi holds one global
// token) and merges into the single-account shape the dashboard already
// consumes. Accounts whose tokens can't be refreshed are skipped and
// reported in `staleAccounts` — never silently faked.
export async function fetchAllDataMulti(accounts, onAccountStart) {
  const merged = [];
  const staleAccounts = [];

  for (const account of accounts) {
    let token = getTokenFor(account.email);
    if (!token) {
      try { token = await signInSilent(account.email); } catch { token = null; }
    }
    if (!token) { staleAccounts.push(account.email); continue; }
    setGapiToken(token);
    onAccountStart && onAccountStart(account.email);
    try {
      const result = await fetchAllData();
      merged.push(tagResult(result, account.email));
    } catch (e) {
      console.warn('fetchAllData failed for', account.email, e);
      staleAccounts.push(account.email);
    }
  }

  // Leave the PRIMARY account's token active for legacy single-account calls.
  const primary = getPrimaryAccount();
  const primaryToken = primary ? getTokenFor(primary.email) : null;
  if (primaryToken) setGapiToken(primaryToken);

  if (merged.length === 0) return null;

  const byDateAsc = (a, b) => new Date(a.date || 0) - new Date(b.date || 0);
  const byDateDesc = (a, b) => new Date(b.date || 0) - new Date(a.date || 0);
  const byStart = (a, b) => new Date(a.startISO || 0) - new Date(b.startISO || 0);

  return {
    calendar: merged.flatMap((r) => r.calendar).sort(byStart),
    gmail: {
      unreadCount: merged.reduce((n, r) => n + (r.gmail?.unreadCount || 0), 0),
      priorityThreads: merged.flatMap((r) => r.gmail?.priorityThreads || []).sort(byDateDesc),
      // Oldest-unanswered-first across ALL inboxes — the approval queue's ranking
      unansweredThreads: merged.flatMap((r) => r.gmail?.unansweredThreads || []).sort(byDateAsc),
      myEmail: merged[0]?.gmail?.myEmail,
      accounts: accounts.map((a) => a.email),
    },
    drive: merged.flatMap((r) => r.drive),
    tasks: merged.flatMap((r) => r.tasks),
    sheets: merged.flatMap((r) => r.sheets),
    stats: {
      emailsToday: merged.reduce((n, r) => n + (r.stats?.emailsToday || 0), 0),
      emailsFlagged: merged.reduce((n, r) => n + (r.stats?.emailsFlagged || 0), 0),
      meetings: merged.reduce((n, r) => n + (r.stats?.meetings || 0), 0),
      meetingsTotal: merged.reduce((n, r) => n + (r.stats?.meetingsTotal || 0), 0),
    },
    staleAccounts,
  };
}
